import React, { useState, useCallback, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import PropTypes from 'prop-types';
import radqcApi from 'dataExchange/radqcApiClient';
import { mamWeeklyTasks } from 'views/entry/mam/components/mamEntry.util';
import { formatDateShort } from 'utils/time.util';
import MamWeeklyOverviewRow from './MamWeeklyOverviewRow';

const useStyles = makeStyles((theme) => ({
  tableTitles: {
    fontWeight: 600,
    color: 'black',
    textAlign: 'left',
    width: '76px',
    height: '50px',
    fontSize: '0.75rem',
    borderBottom: '0.1px solid #0000004a',
    lineHeight: 1.3,
    padding: '0.5rem',
    transition: '0.3s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  tableTitlesInput: {
    color: 'black',
    textAlign: 'center',
    width: '76px',
    height: '50px',
    fontSize: '0.75rem',
    borderBottom: '0.1px solid #0000004a',
    lineHeight: 1.3,
    padding: '0.25rem',
  },
  tableContainer: {
    marginTop: '16px',
    display: 'flex',
    '@media (max-width: 1024px)': {
      margin: 0,
    },
  },
  stickyHeader: {
    width: '10rem',
  },
  rightTables: {
    maxWidth: '100%',
    overflowX: 'auto',
    display: 'flex',
    flexDirection: 'row',
  },
  hoverRow: {
    transition: '0.3s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
}));

function MamWeeklyOverviewTable(props) {
  const { device, history, match } = props;
  const [entries, setEntries] = useState([]);
  const { getAccessTokenSilently } = useAuth0();
  const classes = useStyles();

  // get existing data (currently just adds all existing values to 'trailing' entries)
  const getEntries = useCallback(async () => {
    let receivedEntries;
    try {
      const accessToken = await getAccessTokenSilently();
      receivedEntries = await radqcApi('get', `/entries/mam_wk/device/${device._id}`, { accessToken });
    } catch (err) {
      console.log(err);
    }
    if (receivedEntries) setEntries(receivedEntries);
  }, [device._id, getAccessTokenSilently]);

  useEffect(() => {
    getEntries();
  }, [getEntries]);

  const goToTaskView = (task) => {
    history.push(`${match.url}/weekly/${task}`);
  };

  return (
    <Box className={classes.tableContainer}>
      <Table className={classes.stickyHeader}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableTitles}>Date</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.tableTitles}>Initials</TableCell>
          </TableRow>
          <TableRow onClick={() => goToTaskView(mamWeeklyTasks.VIEW_BOX)}>
            <TableCell className={classes.tableTitles}>Viewboxes and Viewing Conditions (weekly)</TableCell>
          </TableRow>
          <TableRow onClick={() => goToTaskView(mamWeeklyTasks.FLAT_FIELD)}>
            <TableCell className={classes.tableTitles}>Flat-Field Detector Calibration (weekly)</TableCell>
          </TableRow>
          <TableRow onClick={() => goToTaskView(mamWeeklyTasks.ART_EVAL)}>
            <TableCell className={classes.tableTitles}>Artifact Evaluation (weekly)</TableCell>
          </TableRow>
          <TableRow onClick={() => goToTaskView(mamWeeklyTasks.PHANTOM2)}>
            <TableCell className={classes.tableTitles}>Phantom 2D Image (weekly)</TableCell>
          </TableRow>
          <TableRow onClick={() => goToTaskView(mamWeeklyTasks.PHANTOM3)}>
            <TableCell className={classes.tableTitles}>Phantom 3D Image (weekly)</TableCell>
          </TableRow>
          <TableRow onClick={() => goToTaskView(mamWeeklyTasks.SNR_CNR)}>
            <TableCell className={classes.tableTitles}>SNR & CNR Measurements (weekly)</TableCell>
          </TableRow>
          <TableRow onClick={() => goToTaskView(mamWeeklyTasks.COMPRESSION)}>
            <TableCell className={classes.tableTitles}>Compression Thickness Indicator (biweekly)</TableCell>
          </TableRow>
        </TableHead>
      </Table>

      <Box className={classes.rightTables}>
        <Table>
          <TableBody>
            <TableRow>
              {entries.map((entry) => (
                <TableCell className={classes.tableTitlesInput} key={entry._id}>
                  {formatDateShort(entry.date)}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {entries.map((entry) => (
                <TableCell className={classes.tableTitlesInput} key={entry._id}>
                  {entry.initials}
                </TableCell>
              ))}
            </TableRow>
            {Object.values(mamWeeklyTasks).map((task) => (
              <TableRow classes={{ root: classes.hoverRow }} key={task} onClick={() => goToTaskView(task)}>
                <MamWeeklyOverviewRow entries={entries} task={task} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

MamWeeklyOverviewTable.propTypes = {
  device: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default withRouter(MamWeeklyOverviewTable);
